import { useEffect, useState } from 'react';
import api from '../lib/api';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { fmtDateTime, ROLE_LABELS } from '../lib/utils';
import { History, Filter } from 'lucide-react';

const ACTION_LABELS = {
  lead_created: 'Partnership Created',
  lead_assigned: 'Lead Assigned',
  status_changed: 'Status Changed',
  followup_completed: 'Follow-Up Completed',
  lead_archived: 'Record Archived',
  lead_restored: 'Record Restored',
  lead_deleted: 'Record Deleted',
  user_archived: 'User Archived',
  user_restored: 'User Restored',
  user_deleted: 'User Deleted',
  user_activated: 'User Activated',
  user_deactivated: 'User Deactivated',
  user_unlocked: 'User Unlocked',
  user_profile_updated: 'User Profile Updated',
  password_changed: 'Password Changed',
  password_reset_by_admin: 'Password Reset by Admin',
  password_force_reset_flagged: 'Force Password Reset',
  document_uploaded: 'Document Uploaded',
  document_deleted: 'Document Deleted',
  export_leads_xlsx: 'Exported (Excel)',
  export_leads_csv: 'Exported (CSV)',
  export_leads_pdf: 'Exported (PDF)',
  calendar_event_updated: 'Calendar Updated',
  calendar_event_restored: 'Calendar Restored',
  calendar_event_permanent_deleted: 'Calendar Deleted Forever',
};

const ACTION_COLORS = {
  lead_created: 'bg-emerald-100 text-emerald-700',
  lead_assigned: 'bg-blue-100 text-blue-700',
  status_changed: 'bg-amber-100 text-amber-700',
  followup_completed: 'bg-magenta/10 text-magenta',
  lead_archived: 'bg-slate-100 text-slate-700',
  lead_restored: 'bg-cyan-100 text-cyan-700',
  lead_deleted: 'bg-rose-100 text-rose-700',
  user_archived: 'bg-slate-100 text-slate-700',
  user_restored: 'bg-cyan-100 text-cyan-700',
  user_deleted: 'bg-rose-100 text-rose-700',
};

export default function AuditLogPage() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ user_id: 'all', entity_type: 'all', action: 'all', start_date: '', end_date: '' });

  const load = () => {
    const params = new URLSearchParams();
    if (filters.user_id !== 'all') params.append('user_id', filters.user_id);
    if (filters.entity_type !== 'all') params.append('entity_type', filters.entity_type);
    if (filters.action !== 'all') params.append('action', filters.action);
    if (filters.start_date) params.append('start_date', new Date(filters.start_date).toISOString());
    if (filters.end_date) params.append('end_date', new Date(filters.end_date + 'T23:59:59').toISOString());
    params.append('limit', '300');
    api.get(`/audit-log?${params.toString()}`).then((r) => setItems(r.data));
  };

  useEffect(() => { api.get('/users').then((r) => setUsers(r.data)); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-magenta" />
          <h3 className="font-heading font-bold text-navy">Filter Activity Log</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <Label className="text-xs">User</Label>
            <Select value={filters.user_id} onValueChange={(v) => setFilters({ ...filters, user_id: v })}>
              <SelectTrigger data-testid="audit-user-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Entity</Label>
            <Select value={filters.entity_type} onValueChange={(v) => setFilters({ ...filters, entity_type: v })}>
              <SelectTrigger data-testid="audit-entity-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="lead">Partnership / Lead</SelectItem>
                <SelectItem value="user">User</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Action</Label>
            <Select value={filters.action} onValueChange={(v) => setFilters({ ...filters, action: v })}>
              <SelectTrigger data-testid="audit-action-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {Object.entries(ACTION_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50">
              {['When', 'User', 'Role', 'Action', 'Summary', 'IP', 'Browser'].map((h) => (
                <th key={h} className="text-left text-xs font-heading uppercase tracking-wider text-slate-500 px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="text-center py-16 text-slate-500 text-sm"><History className="w-10 h-10 mx-auto mb-2 text-slate-300" />No activity matches your filters</td></tr>
            )}
            {items.map((a) => (
              <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`audit-row-${a.id}`}>
                <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">{fmtDateTime(a.created_at)}</td>
                <td className="px-4 py-3 text-sm font-semibold text-navy">{a.user_name}</td>
                <td className="px-4 py-3"><span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{ROLE_LABELS[a.user_role] || a.user_role}</span></td>
                <td className="px-4 py-3"><span className={`text-[10px] font-semibold px-2 py-1 rounded-full ${ACTION_COLORS[a.action] || 'bg-slate-100 text-slate-700'}`}>{ACTION_LABELS[a.action] || a.action}</span></td>
                <td className="px-4 py-3 text-sm text-slate-700">{a.summary}</td>
                <td className="px-4 py-3 text-xs text-slate-500 font-mono">{a.extra?.ip || '—'}</td>
                <td className="px-4 py-3 text-xs text-slate-500 truncate max-w-[180px]" title={a.extra?.browser}>{a.extra?.browser || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
